import jsPDF from "jspdf";
import QRCode from "qrcode";

/**
 * Etiquetas de identificação para inventário físico: QR Code + código de inventário (TI-000123),
 * patrimônio e nomenclatura. O QR Code aponta para o ativo no sistema (/ti/inventario?codigo=...);
 * o texto do código também pode ser digitado/lido por leitor de código de barras na pesquisa.
 */

// Folha A4 com etiquetas 63,5 x 33,9 mm (3 colunas x 8 linhas) e etiqueta única para impressoras térmicas.
const ETIQUETA = { largura: 63.5, altura: 33.9 };
const FOLHA = { colunas: 3, linhas: 8, margemX: 7.5, margemY: 12.5 };

export function urlDoAtivo(codigo) {
    return `${window.location.origin}/ti/inventario?codigo=${encodeURIComponent(codigo)}`;
}

function cortar(doc, texto, larguraMax) {
    const t = (texto ?? "").toString();
    if (!t) return "";
    return doc.splitTextToSize(t, larguraMax)[0];
}

async function desenharEtiqueta(doc, ativo, x, y, comBorda) {
    const codigo = ativo.codigoInventario || `TI-${String(ativo.id).padStart(6, "0")}`;
    const qr = await QRCode.toDataURL(urlDoAtivo(codigo), { margin: 0, width: 240, errorCorrectionLevel: "M" });

    if (comBorda) {
        doc.setDrawColor(190);
        doc.setLineWidth(0.1);
        doc.rect(x, y, ETIQUETA.largura, ETIQUETA.altura);
    }

    const lado = 24;
    doc.addImage(qr, "PNG", x + 3, y + (ETIQUETA.altura - lado) / 2, lado, lado);

    const tx = x + 3 + lado + 3;
    const largura = ETIQUETA.largura - (tx - x) - 2;
    let ty = y + 5;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.5);
    doc.setTextColor(90);
    doc.text("YORK - TI", tx, ty, { baseline: "top" });
    ty += 3.6;

    doc.setFontSize(11);
    doc.setTextColor(0);
    doc.text(codigo, tx, ty, { baseline: "top" });
    ty += 5.4;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    const linhas = [
        ativo.patrimonio ? `Patr.: ${ativo.patrimonio}` : null,
        ativo.nomenclatura ? cortar(doc, ativo.nomenclatura, largura) : null,
        [ativo.tipoDispositivoNome, ativo.marcaNome].filter(Boolean).join(" ") || null,
        ativo.filialNome || null,
    ].filter(Boolean);
    linhas.slice(0, 4).forEach((linha) => {
        doc.text(cortar(doc, linha, largura), tx, ty, { baseline: "top" });
        ty += 3.5;
    });
}

/**
 * Gera e baixa o PDF de etiquetas. Com um único ativo, a página tem o tamanho da etiqueta
 * (impressora térmica); com vários, usa folha A4 de etiquetas 3x8.
 */
export async function gerarEtiquetas(ativos) {
    if (!ativos || ativos.length === 0) return;

    if (ativos.length === 1) {
        const doc = new jsPDF({ unit: "mm", format: [ETIQUETA.largura, ETIQUETA.altura], orientation: "landscape" });
        await desenharEtiqueta(doc, ativos[0], 0, 0, false);
        doc.save(`etiqueta-${ativos[0].codigoInventario || ativos[0].id}.pdf`);
        return;
    }

    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const porFolha = FOLHA.colunas * FOLHA.linhas;
    for (let i = 0; i < ativos.length; i += 1) {
        if (i > 0 && i % porFolha === 0) doc.addPage();
        const posicao = i % porFolha;
        const coluna = posicao % FOLHA.colunas;
        const linha = Math.floor(posicao / FOLHA.colunas);
        const x = FOLHA.margemX + coluna * ETIQUETA.largura;
        const y = FOLHA.margemY + linha * ETIQUETA.altura;
        // eslint-disable-next-line no-await-in-loop
        await desenharEtiqueta(doc, ativos[i], x, y, true);
    }
    doc.save(`etiquetas-inventario-ti.pdf`);
}
